import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface IncidentHistoryItem {
  id: string;
  incidentId: number;
  submittedAt: string;
  vehicleNumber: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  operatorName: string;
  incidentType: string;
  incidentDate: string;
  status: 'submitted' | 'draft' | 'failed';
  photoCount: number;
  damageCount: number;
}

interface HistoryState {
  items: IncidentHistoryItem[];
  maxItems: number;
  lastViewedAt: string | null;
}

const initialState: HistoryState = {
  items: [],
  maxItems: 10,
  lastViewedAt: null
};

const historySlice = createSlice({
  name: 'history',
  initialState,
  reducers: {
    addHistoryItem: (state, action: PayloadAction<IncidentHistoryItem>) => {
      // Add new item at the beginning
      state.items.unshift(action.payload);
      
      // Keep only the most recent items
      if (state.items.length > state.maxItems) {
        state.items = state.items.slice(0, state.maxItems);
      }
    },
    
    updateHistoryItem: (state, action: PayloadAction<{ id: string; updates: Partial<IncidentHistoryItem> }>) => {
      const index = state.items.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          ...action.payload.updates
        };
      }
    },
    
    removeHistoryItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    },
    
    clearHistory: (state) => {
      state.items = [];
      state.lastViewedAt = new Date().toISOString();
    },
    
    setMaxItems: (state, action: PayloadAction<number>) => {
      state.maxItems = action.payload;
      if (state.items.length > state.maxItems) {
        state.items = state.items.slice(0, state.maxItems);
      }
    },
    
    markAsViewed: (state) => {
      state.lastViewedAt = new Date().toISOString();
    },
    
    reorderHistory: (state) => {
      // Sort by submission date, most recent first
      state.items.sort((a, b) => 
        new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
      );
    }
  }
});

export const {
  addHistoryItem,
  updateHistoryItem,
  removeHistoryItem,
  clearHistory,
  setMaxItems,
  markAsViewed,
  reorderHistory
} = historySlice.actions;

export default historySlice.reducer;

// Selectors
export const selectAllHistory = (state: { history: HistoryState }) => state.history.items;

export const selectRecentHistory = (state: { history: HistoryState }, count: number = 5) => 
  state.history.items.slice(0, count);

export const selectHistoryByStatus = (state: { history: HistoryState }, status: IncidentHistoryItem['status']) =>
  state.history.items.filter(item => item.status === status);

export const selectHistoryByVehicle = (state: { history: HistoryState }, vehicleNumber: string) =>
  state.history.items.filter(item => item.vehicleNumber === vehicleNumber);

export const selectHistoryStats = (state: { history: HistoryState }) => {
  const items = state.history.items;
  return {
    total: items.length,
    submitted: items.filter(i => i.status === 'submitted').length,
    drafts: items.filter(i => i.status === 'draft').length,
    failed: items.filter(i => i.status === 'failed').length,
    lastSubmission: items.length > 0 ? items[0].submittedAt : null
  };
};